import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { stripHtml } from '../../components/richtext/RichText';
import { useCategoryManagerPane } from '../../components/categories/CategoryManager';
import BankLocationPicker, { EMPTY_BANK_LOCATION, bankLocationToExam, bankLocationFromExam } from '../../components/quiz/BankLocationPicker';
import { motion } from 'framer-motion';
import {
  Trash,
  Clock,
  ListChecks,
  Gear,
  Eye,
  EyeSlash,
  FloppyDisk,
  CheckCircle,
  X,
  MagnifyingGlass,
  DotsSixVertical,
  ChartBar,
  Info,
  Shuffle,
  CalendarBlank,
  FolderPlus,
} from '@phosphor-icons/react';

const ASSESSMENT_TYPE_LABELS = {
  quiz: 'Quiz',
  exam: 'Exam',
  practice: 'Practice Test',
  survey: 'Survey',
};

const QUESTION_TYPE_LABELS = {
  'MCQ_SINGLE': 'MCQ (Single)',
  'MCQ_MULTI': 'MCQ (Multi)',
  'TRUE_FALSE': 'True/False',
  'FILL_BLANK': 'Fill Blank',
  'SHORT_ANSWER': 'Short Answer',
  'ESSAY': 'Essay',
  'NUMERIC': 'Numeric',
  'MATCH': 'Matching',
  'ORDER': 'Ordering',
  'FILE_UPLOAD': 'File Upload',
};

export default function ExamBuilder() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { examId } = useParams();
  
  const [loading, setLoading] = useState(!!examId);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  // shared categories (question bank tree) for "Push to the Question bank"
  const categoryMgr = useCategoryManagerPane({ api });
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    course_id: null,
    node_id: null,
    assessment_type: 'quiz',
    category_id: '',
    instructions: '',
    is_published: false,
    in_question_bank: false,
    bank: { ...EMPTY_BANK_LOCATION },
    settings: {
      time_limit_minutes: null,
      max_attempts: 1,
      passing_score: 60,
      shuffle_questions: false,
      shuffle_options: false,
      show_results_immediately: true,
      show_correct_answers: false,
      show_explanations: false,
      allow_review: true,
      auto_submit_on_timeout: true,
      allow_resume: true,
      availability_start: '',
      availability_end: '',
    },
    questions: [], // Array of { question_id, order, points_override }
    random_selection: false,
    random_count: 10,
    random_filters: {
      difficulty: 'all',
      topic: '',
    }
  });

  const fetchQuestions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      params.append('is_active', 'true');
      
      const res = await api.get(`/question-bank?${params.toString()}`);
      console.log('Fetched questions:', res.data?.length || 0);
      setQuestions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
      setQuestions([]);
    }
  }, [api]);

  const fetchExam = async () => {
    try {
      const res = await api.get(`/exams/${examId}`);
      const exam = res.data;
      const cats = (await api.get('/categories').catch(() => ({ data: [] }))).data || [];
      setForm({
        title: exam.title || '',
        description: exam.description || '',
        course_id: exam.course_id || '',
        node_id: exam.node_id || null,
        assessment_type: exam.assessment_type || 'quiz',
        category_id: exam.category_id || '',
        instructions: exam.instructions || '',
        is_published: exam.is_published || false,
        in_question_bank: !!exam.in_question_bank,
        bank: bankLocationFromExam(exam, cats),
        settings: {
          time_limit_minutes: exam.settings?.time_limit_minutes || null,
          max_attempts: exam.settings?.max_attempts || 1,
          passing_score: exam.settings?.passing_score || 60,
          shuffle_questions: exam.settings?.shuffle_questions || false,
          shuffle_options: exam.settings?.shuffle_options || false,
          show_results_immediately: exam.settings?.show_results_immediately ?? true,
          show_correct_answers: exam.settings?.show_correct_answers || false,
          show_explanations: exam.settings?.show_explanations || false,
          allow_review: exam.settings?.allow_review ?? true,
          auto_submit_on_timeout: exam.settings?.auto_submit_on_timeout ?? true,
          allow_resume: exam.settings?.allow_resume ?? true,
          availability_start: exam.settings?.availability_start || '',
          availability_end: exam.settings?.availability_end || '',
        },
        questions: exam.questions || [],
        random_selection: exam.random_selection || false,
        random_count: exam.random_count || 10,
        random_filters: exam.random_filters ? { 
          difficulty: exam.random_filters.difficulty || 'all', 
          topic: exam.random_filters.topic || '' 
        } : { difficulty: 'all', topic: '' }
      });
    } catch (error) {
      toast.error('Failed to load exam');
      navigate('/instructor/exams');
    } finally {
      setLoading(false);
    }
  };

  useTabLabel(!examId ? 'New exam' : (form.title ? `Edit: ${form.title}` : null));

  useEffect(() => {
    fetchQuestions();
    if (examId) {
      fetchExam();
    }
  }, [examId]);

  // Persist a given form state (used by Save and the Publish/Unpublish toggle)
  const persistExam = async (data, publish = false) => {
    if (!data.title.trim()) {
      toast.error('Exam title is required');
      return;
    }

    // Only require questions for publishing, not for draft
    if (publish && !data.random_selection && data.questions.length === 0) {
      toast.error('Please add at least one question before publishing');
      return;
    }
    if (data.in_question_bank && !data.bank?.subject_id) {
      toast.error('Choose the subject this quiz should appear under in the Question bank');
      setActiveTab('details');
      return;
    }

    setSaving(true);
    try {
      // Convert 'all' difficulty back to empty string for backend
      const randomFilters = {
        ...data.random_filters,
        difficulty: data.random_filters.difficulty === 'all' ? '' : data.random_filters.difficulty
      };

      const payload = {
        ...data,
        random_filters: randomFilters,
        is_published: publish ? true : data.is_published,
        // "" takes it out of every group; the server reads an empty string as "clear".
        category_id: data.category_id || '',
        in_question_bank: !!data.in_question_bank,
        ...(data.in_question_bank ? bankLocationToExam(data.bank) : { bank_category_id: '', bank_course_id: '', bank_node_id: '' }),
        bank: undefined,
        settings: {
          ...data.settings,
          time_limit_minutes: data.settings.time_limit_minutes ? parseInt(data.settings.time_limit_minutes) : null,
          max_attempts: parseInt(data.settings.max_attempts) || 1,
          passing_score: parseFloat(data.settings.passing_score) || 60,
        }
      };

      if (examId) {
        await api.put(`/exams/${examId}`, payload);
        toast.success('Exam updated');
        if (publish) setForm(prev => ({ ...prev, is_published: true }));
      } else {
        const res = await api.post('/exams', payload);
        toast.success('Exam created');
        navigate(`/instructor/exams/${res.data.id}`);
        return;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save exam');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (publish = false) => persistExam(form, publish);

  // Publish / Unpublish toggle: flip the flag and persist in one step
  const togglePublish = async () => {
    if (form.is_published) {
      const next = { ...form, is_published: false };
      setForm(next);
      await persistExam(next, false);
    } else {
      await handleSave(true);
    }
  };

  const handleDelete = async () => {
    if (!examId) return;
    if (!window.confirm(`Delete exam "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/exams/${examId}`);
      toast.success('Exam deleted');
      navigate('/instructor/exams');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete exam');
    }
  };

  const addQuestion = (question) => {
    if (form.questions.find(q => q.question_id === question.id)) {
      toast.warning('Question already added');
      return;
    }
    setForm({
      ...form,
      questions: [
        ...form.questions,
        { question_id: question.id, order: form.questions.length, points_override: null, _question: question }
      ]
    });
  };

  const removeQuestion = (questionId) => {
    setForm({
      ...form,
      questions: form.questions.filter(q => q.question_id !== questionId)
    });
  };

  const updateQuestionOrder = (fromIndex, toIndex) => {
    const newQuestions = [...form.questions];
    const [moved] = newQuestions.splice(fromIndex, 1);
    newQuestions.splice(toIndex, 0, moved);
    newQuestions.forEach((q, i) => q.order = i);
    setForm({ ...form, questions: newQuestions });
  };

  const filteredQuestions = questions.filter(q => {
    if (questionSearch) {
      const search = questionSearch.toLowerCase();
      return stripHtml(q.question_text).toLowerCase().includes(search) ||
             q.topic?.toLowerCase().includes(search);
    }
    return true;
  });

  // Get full question data for display
  const selectedQuestions = form.questions.map(sq => {
    const fullQuestion = questions.find(q => q.id === sq.question_id) || sq._question;
    return { ...sq, question: fullQuestion };
  });

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Exam" loading={loading} onBack={() => navigate('/instructor/exams')} />
      </DashboardLayout>
    );
  }

  const isNew = !examId;

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="exam-builder-page"
        title={form.title || 'New exam'}
        subtitle={`${ASSESSMENT_TYPE_LABELS[form.assessment_type] || form.assessment_type} · ${form.questions.length} questions selected`}
        badge={!isNew && <StatusBadge value={form.is_published ? 'published' : 'draft'} />}
        onBack={() => navigate('/instructor/exams')}
        onRefresh={isNew ? undefined : fetchExam}
        refreshing={saving}
        side={categoryMgr.side}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : isNew ? 'Create exam' : 'Save', icon: FloppyDisk, onClick: () => handleSave(false), disabled: saving, testid: 'save-exam-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: () => navigate('/instructor/exams') },
        ]}
        secondary={[
          { key: 'preview', label: 'Preview', icon: Eye, onClick: () => navigate(`/instructor/exams/${examId}/preview`), disabled: isNew, testid: 'preview-exam-btn' },
          { key: 'report', label: 'Report', icon: ChartBar, onClick: () => navigate(`/instructor/exams/${examId}/report`), disabled: isNew },
          { key: 'publish', label: form.is_published ? 'Unpublish' : 'Publish', icon: form.is_published ? EyeSlash : CheckCircle, onClick: togglePublish, disabled: saving, testid: 'publish-exam-btn' },
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: handleDelete, disabled: isNew, testid: 'delete-exam-btn' },
        ]}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="questions">Questions</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <FormSection title="Exam Details" icon={Info} description="Basic information about your exam" bodyClassName="p-3 space-y-3">
                <div className="form-grid">
                  <div className="field sm:col-span-2">
                    <Label>Title *</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Enter exam title"
                      data-testid="exam-title-input"
                    />
                  </div>
                  <div className="field">
                    <Label>Type</Label>
                    <Select value={form.assessment_type} onValueChange={(v) => setForm({ ...form, assessment_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quiz">Quiz</SelectItem>
                        <SelectItem value="exam">Exam</SelectItem>
                        <SelectItem value="practice">Practice Test</SelectItem>
                        <SelectItem value="survey">Survey</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="field">
                    {/* The group this exam belongs to: 10th Class ▸ Science. The same
                        hierarchy Courses and the Question bank use, so a group is
                        written down once and means the same thing everywhere. */}
                    <Label htmlFor="exam-group" data-find="Group">Group</Label>
                    <div className="flex items-center gap-1.5">
                      <select id="exam-group" className="form-select" value={form.category_id || ''}
                        onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                        data-testid="exam-group">
                        <option value="">No group</option>
                        {(categoryMgr.categories || []).map((c) => (
                          <option key={c.id} value={c.id}>{'\u00A0\u00A0'.repeat(c.depth)}{c.name}</option>
                        ))}
                      </select>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8 flex-shrink-0"
                        title="Add or edit groups" onClick={() => categoryMgr.open()} data-testid="manage-exam-groups">
                        <FolderPlus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="field-hint">
                      {form.category_id
                        ? (categoryMgr.categories || []).find((c) => c.id === form.category_id)?.path
                        : 'Optional. Groups can hold sub-groups — 10th Class ▸ Science.'}
                    </p>
                  </div>
                  <div className="field">
                    <Label>Visibility</Label>
                    <div className="h-8 flex items-center gap-2">
                      <Switch
                        checked={form.is_published}
                        onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                      />
                      <span className="text-[13px]">Published (visible to learners)</span>
                    </div>
                  </div>
                  <div className="field sm:col-span-2 rounded border border-border bg-muted/30 p-2.5 space-y-2" data-testid="bank-listing">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.in_question_bank} onCheckedChange={(v) => setForm({ ...form, in_question_bank: v })} data-testid="in-question-bank" />
                      <span className="text-[13px] font-medium">Push to the Question bank</span>
                      <span className="field-hint">— list this {form.assessment_type === 'exam' ? 'exam' : 'quiz'} in the bank's tree; its uncategorised questions are filed in the same place</span>
                    </div>
                    {form.in_question_bank && (
                      <BankLocationPicker api={api} value={form.bank} onChange={(loc) => setForm({ ...form, bank: loc })} compact />
                    )}
                  </div>
                  <div className="field sm:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Brief description of the exam"
                      rows={3}
                    />
                  </div>
                  <div className="field sm:col-span-2">
                    <Label>Instructions for Students</Label>
                    <Textarea
                      value={form.instructions}
                      onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                      placeholder="Instructions shown to students before starting..."
                      rows={3}
                    />
                  </div>
                </div>
            </FormSection>
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Question Bank */}
              <FormSection title="Question Bank" icon={ListChecks} description="Click questions to add them to your exam">
                  <div className="relative mb-4">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search questions..."
                      className="pl-10"
                      value={questionSearch}
                      onChange={(e) => setQuestionSearch(e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {questions.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No questions found. Create questions in the Question Bank first.
                      </p>
                    ) : filteredQuestions.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No questions match your search.
                      </p>
                    ) : (
                      filteredQuestions.map((q) => {
                        const isSelected = form.questions.some(sq => sq.question_id === q.id);
                        return (
                          <div
                            key={q.id}
                            onClick={() => !isSelected && addQuestion(q)}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border-primary/30 cursor-not-allowed' 
                                : 'hover:border-primary/50'
                            }`}
                            data-testid={`question-bank-item-${q.id}`}
                          >
                            <p className="text-sm line-clamp-2">{stripHtml(q.question_text)}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {QUESTION_TYPE_LABELS[q.question_type] || q.question_type}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">{q.points} pts</Badge>
                              {isSelected && (
                                <Badge className="bg-primary text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Added
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
              </FormSection>

              {/* Selected Questions */}
              <FormSection
                title={`Selected Questions (${form.questions.length})`}
                icon={CheckCircle}
                actions={
                  <span className="text-xs text-muted-foreground">
                    Total: {selectedQuestions.reduce((sum, q) => sum + (q.points_override || q.question?.points || 0), 0)} pts
                  </span>
                }
              >
                  {form.questions.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ListChecks className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No questions added yet</p>
                      <p className="text-sm">Click questions from the bank to add them</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedQuestions.map((sq, index) => (
                        <motion.div
                          key={sq.question_id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center gap-2 p-3 border rounded-lg group"
                          data-testid={`selected-question-${sq.question_id}`}
                        >
                          <DotsSixVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                          <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm line-clamp-1">
                              {sq.question ? stripHtml(sq.question.question_text) : 'Loading...'}
                            </p>
                            <p className="field-hint">
                              {QUESTION_TYPE_LABELS[sq.question?.question_type]} • {sq.question?.points || 0} pts
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100"
                            onClick={() => removeQuestion(sq.question_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </motion.div>
                      ))}
                    </div>
                  )}
              </FormSection>
            </div>

            {/* Random Selection Option */}
            <FormSection
              title="Random Question Selection"
              icon={Shuffle}
              actions={
                <Switch
                  checked={form.random_selection}
                  onCheckedChange={(v) => setForm({ ...form, random_selection: v })}
                />
              }
            >
                <p className="text-sm text-muted-foreground">
                  Randomly select questions from the bank for each attempt
                </p>

                {form.random_selection && (
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                    <div>
                      <Label>Number of Questions</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.random_count}
                        onChange={(e) => setForm({ ...form, random_count: parseInt(e.target.value) || 10 })}
                      />
                    </div>
                    <div>
                      <Label>Filter by Difficulty</Label>
                      <Select 
                        value={form.random_filters.difficulty || 'all'} 
                        onValueChange={(v) => setForm({ ...form, random_filters: { ...form.random_filters, difficulty: v } })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="easy">Easy</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="hard">Hard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Filter by Topic</Label>
                      <Input
                        value={form.random_filters.topic || ''}
                        onChange={(e) => setForm({ ...form, random_filters: { ...form.random_filters, topic: e.target.value } })}
                        placeholder="Any topic"
                      />
                    </div>
                  </div>
                )}
            </FormSection>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Time & Attempts */}
              <FormSection title="Time & Attempts" icon={Clock} bodyClassName="p-3 space-y-3">
                  <div>
                    <Label>Time Limit (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="No limit"
                      value={form.settings.time_limit_minutes || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, time_limit_minutes: e.target.value } 
                      })}
                      data-testid="time-limit-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty for no time limit
                    </p>
                  </div>

                  <div>
                    <Label>Maximum Attempts</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.settings.max_attempts}
                      onChange={(e) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, max_attempts: parseInt(e.target.value) || 1 } 
                      })}
                    />
                  </div>

                  <div>
                    <Label>Passing Score (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={form.settings.passing_score}
                      onChange={(e) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, passing_score: parseFloat(e.target.value) || 0 } 
                      })}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Auto-submit on timeout</Label>
                      <Switch
                        checked={form.settings.auto_submit_on_timeout}
                        onCheckedChange={(v) => setForm({ 
                          ...form, 
                          settings: { ...form.settings, auto_submit_on_timeout: v } 
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Allow resume if interrupted</Label>
                      <Switch
                        checked={form.settings.allow_resume}
                        onCheckedChange={(v) => setForm({
                          ...form,
                          settings: { ...form.settings, allow_resume: v }
                        })}
                      />
                    </div>
                  </div>
              </FormSection>

              {/* Display Settings */}
              <FormSection title="Display Settings" icon={Gear} bodyClassName="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Shuffle Questions</Label>
                      <p className="field-hint">Randomize question order</p>
                    </div>
                    <Switch
                      checked={form.settings.shuffle_questions}
                      onCheckedChange={(v) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, shuffle_questions: v } 
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Shuffle Options</Label>
                      <p className="field-hint">Randomize answer options</p>
                    </div>
                    <Switch
                      checked={form.settings.shuffle_options}
                      onCheckedChange={(v) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, shuffle_options: v } 
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Results Immediately</Label>
                      <p className="field-hint">Show score after submission</p>
                    </div>
                    <Switch
                      checked={form.settings.show_results_immediately}
                      onCheckedChange={(v) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, show_results_immediately: v } 
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Correct Answers</Label>
                      <p className="field-hint">Reveal correct answers after</p>
                    </div>
                    <Switch
                      checked={form.settings.show_correct_answers}
                      onCheckedChange={(v) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, show_correct_answers: v } 
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Explanations</Label>
                      <p className="field-hint">Show answer explanations</p>
                    </div>
                    <Switch
                      checked={form.settings.show_explanations}
                      onCheckedChange={(v) => setForm({ 
                        ...form, 
                        settings: { ...form.settings, show_explanations: v } 
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow Review</Label>
                      <p className="field-hint">Let students review answers</p>
                    </div>
                    <Switch
                      checked={form.settings.allow_review}
                      onCheckedChange={(v) => setForm({
                        ...form,
                        settings: { ...form.settings, allow_review: v }
                      })}
                    />
                  </div>
              </FormSection>

              {/* Availability */}
              <FormSection title="Availability Window" icon={CalendarBlank} description="Optionally restrict when the exam can be taken" className="lg:col-span-2">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date/Time</Label>
                      <Input
                        type="datetime-local"
                        value={form.settings.availability_start?.slice(0, 16) || ''}
                        onChange={(e) => setForm({ 
                          ...form, 
                          settings: { ...form.settings, availability_start: e.target.value ? new Date(e.target.value).toISOString() : '' } 
                        })}
                      />
                    </div>
                    <div>
                      <Label>End Date/Time</Label>
                      <Input
                        type="datetime-local"
                        value={form.settings.availability_end?.slice(0, 16) || ''}
                        onChange={(e) => setForm({ 
                          ...form, 
                          settings: { ...form.settings, availability_end: e.target.value ? new Date(e.target.value).toISOString() : '' }
                        })}
                      />
                    </div>
                  </div>
              </FormSection>
            </div>
          </TabsContent>
        </Tabs>
      </RecordPage>
    </DashboardLayout>
  );
}
